import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { IndianRupee, ShoppingBag, Users, AlertCircle, Loader2, History, Trophy, Search } from 'lucide-react';

export default function LabsStoreDashboardTab({ dashboard, loading, filters, onFiltersChange }) {
  const [localSearch, setLocalSearch] = useState(filters?.search || '');

  if (loading || !dashboard) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  }

  const stats = [
    { label: 'Total Orders', value: dashboard.total_orders, icon: ShoppingBag, color: 'bg-blue-100 text-blue-600' },
    { label: 'Total Earnings', value: `₹${dashboard.total_earnings}`, icon: IndianRupee, color: 'bg-teal-100 text-teal-600' },
    { label: 'Total Spent', value: `₹${dashboard.total_spent}`, icon: IndianRupee, color: 'bg-slate-100 text-slate-600' },
    { label: 'Due Amount', value: `₹${dashboard.total_due}`, icon: AlertCircle, color: 'bg-amber-100 text-amber-600' },
  ];

  const applyFilters = () => onFiltersChange?.({ ...filters, search: localSearch });

  return (
    <div className="space-y-6" data-testid="labs-store-dashboard-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search patient, phone, order, test..."
            className="pl-9"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            data-testid="labs-store-dashboard-search"
          />
        </div>
        <Input type="date" className="w-40" value={filters?.date_from || ''} onChange={(e) => onFiltersChange?.({ ...filters, date_from: e.target.value })} data-testid="labs-store-dashboard-date-from" />
        <Input type="date" className="w-40" value={filters?.date_to || ''} onChange={(e) => onFiltersChange?.({ ...filters, date_to: e.target.value })} data-testid="labs-store-dashboard-date-to" />
        <Button size="sm" onClick={applyFilters} data-testid="labs-store-dashboard-filter-btn">Apply</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-400">{s.label}</p>
                <p className="text-lg font-bold">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Orders By Vendor</h3>
          {dashboard.by_vendor.length === 0 ? (
            <p className="text-sm text-slate-400">No order history yet.</p>
          ) : (
            <div className="space-y-2">
              {dashboard.by_vendor.map((v) => (
                <div key={v.vendor_id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg text-sm">
                  <span className="font-medium">{v.vendor_name}</span>
                  <span className="text-slate-500">{v.orders} orders</span>
                  <span className="text-slate-500">Spent ₹{Math.round(v.spent)}</span>
                  <span className="text-teal-700 font-medium">Earned ₹{Math.round(v.earnings)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-amber-700">
            <Trophy className="w-4 h-4" /> Referral Leaderboard - {dashboard.leaderboard_month}
          </h3>
          {(!dashboard.monthly_leaderboard || dashboard.monthly_leaderboard.length === 0) ? (
            <p className="text-sm text-slate-400">No vendor-booked referrals this month yet.</p>
          ) : (
            <div className="space-y-2">
              {dashboard.monthly_leaderboard.map((v) => (
                <div key={v.vendor_id} className="flex items-center justify-between p-2.5 bg-amber-50 rounded-lg text-sm" data-testid={`labs-store-leaderboard-${v.vendor_id}`}>
                  <div className="flex items-center gap-2">
                    <Badge className={v.rank === 1 ? 'bg-amber-400 text-white' : v.rank === 2 ? 'bg-slate-300 text-slate-800' : v.rank === 3 ? 'bg-orange-300 text-orange-900' : 'bg-slate-100 text-slate-500'}>
                      #{v.rank}
                    </Badge>
                    <span className="font-medium">{v.vendor_name}</span>
                  </div>
                  <span className="text-slate-500">{v.orders} referral(s)</span>
                  <span className="text-teal-700 font-semibold">₹{v.earnings} earned</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><History className="w-4 h-4" /> Patient History - Detailed</h3>
          {(!dashboard.patient_history || dashboard.patient_history.length === 0) ? (
            <p className="text-sm text-slate-400">No booking history yet.</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {dashboard.patient_history.map((p) => (
                <div key={p.order_id} className="p-3 bg-slate-50 rounded-lg text-sm" data-testid={`labs-store-patient-history-${p.order_id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <span className="font-medium">{p.patient_name || 'Unknown'}</span>
                      <span className="text-slate-400 ml-2">{p.patient_phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{p.created_at ? new Date(p.created_at).toLocaleString() : ''}</Badge>
                      <Badge className={p.report_status === 'ready' ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-slate-200 text-slate-600 text-[10px]'}>
                        {p.report_status === 'ready' ? 'Report Ready' : 'Report Pending'}
                      </Badge>
                      <Badge className={p.payment_status === 'due' ? 'bg-amber-100 text-amber-700 text-[10px]' : 'bg-green-100 text-green-700 text-[10px]'}>
                        {p.payment_status === 'due' ? 'Due' : 'Paid'}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-slate-500 text-xs mt-1">{p.vendor_name} • {(p.tests || []).join(', ')}</p>
                  <p className="text-teal-700 text-xs font-medium mt-0.5">Earning: ₹{p.doctor_earning}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Unique Patients ({dashboard.unique_patients})</h3>
          {dashboard.patients.length === 0 ? (
            <p className="text-sm text-slate-400">No patients booked yet.</p>
          ) : (
            <div className="space-y-2">
              {dashboard.patients.map((p, idx) => (
                <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg text-sm">
                  <span className="font-medium">{p.patient_name}</span>
                  <span className="text-slate-500">{p.patient_phone}</span>
                  <span className="text-slate-500">{p.orders} booking(s)</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
